import React from "react";
import { gql, useQuery } from "urql";

import { twoDecPriceFormat } from "../../../common/src/utils/FormatFunctions";
import { DateForm } from "../../../components/src/dateForm";

import { GET_TRANSFER_AGENT_ORDERS_SUMMARY } from "../../../graphql/src/transferagent/queries";
import { CurrencyEnum, Nav, NavFundClass, Party, PartyFundInfoClass, TransferAgentOrder, TransferAgentOrderEnum } from "../types.generated";
import { SelectColumnFilter } from "../../../components/src/react-table/ReactTableFilters";
import { ReactTable } from "../../../components/src/react-table/ReactTable";
import { useQueryState } from "../../../components/src/use-query-state";

const getNavs = gql`
    query getNavs($date: GraphQLDateString) {
        navs(date: $date, lastOnly: true) {
            clientId
            data {
                classes {
                    name
                    navCurrency
                    navPerUnit
                    fxRate
                }
            }
            date
        }
    }
`;

export function TransferAgentOrdersSummaryPage(): React.ReactElement {
    const [tradeDate] = useQueryState("tradeDate", new Date().toISOString().slice(0, 10));

    const [{ fetching: loadingOrders, error: errorOrders, data: dataOrders }] = useQuery({
        query: GET_TRANSFER_AGENT_ORDERS_SUMMARY,
        variables: { date: tradeDate },
        requestPolicy: "network-only"
    });
    const [{ fetching, error, data }] = useQuery({ query: getNavs, variables: { date: tradeDate }, requestPolicy: "network-only" });

    const columns = React.useMemo(
        () => [
            {
                header: "Fund name",
                accessorKey: "name",
                filter: SelectColumnFilter,
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return (
                        <a href={`/parties/${row.original.clientId}`} target={"_blank"} rel="noreferrer">
                            {cellProps.getValue()}
                        </a>
                    );
                },
                size: 250
            },
            {
                header: "Netted order value",
                accessorKey: "nettedOrderValue",
                filterFn: "startsWith",
                cell: (cellProps) => (
                    <div style={{ textAlign: "right", paddingRight: "2px" }}>{twoDecPriceFormat(cellProps.getValue())}</div>
                ),
                size: 130
            },
            {
                header: "Currency",
                accessorKey: "currency",
                filter: SelectColumnFilter,
                size: 80
            },
            {
                header: "Last timestamp",
                accessorKey: "lastTimestamp",
                cell: (cellProps) => <div style={{ textAlign: "center" }}>{cellProps.getValue()}</div>,
                size: 150,
                className: "nowrap"
            },
            {
                header: "",
                accessorKey: "-",
                cell: () => <div></div>,
                filter: null,
                size: "auto"
            }
        ],
        []
    );

    if (fetching || loadingOrders) return <div>Loading...</div>;
    if (error) return <div>Error! ${error.message}`</div>;
    if (errorOrders) return <div>Error! ${errorOrders.message}`</div>;

    const datarows = [];

    // sum orders across classes and use estimated NAV when units are used. start from parties to ensure all of them in the result
    dataOrders.parties.forEach((fund: Party) => {
        const fundOrders: TransferAgentOrder[] = dataOrders.transferAgentOrders.filter((o: TransferAgentOrder) => o.clientId === fund._id);
        // no orders case
        if (fundOrders.length === 0) {
            datarows.push({
                name: fund.name,
                clientId: fund._id,
                nettedOrderValue: 0,
                nettedOrderValueSek: 0,
                currency: "",
                lastTimestamp: ""
            });
            return;
        }
        // a fund could have orders in different currencies
        const currencies = [...new Set(fundOrders.map((o: TransferAgentOrder) => o.currency))];
        // calculate nettedOrderValue per currency
        const rows = currencies.map((currency) => {
            const ordersWithCurrency: TransferAgentOrder[] = fundOrders.filter((o) => o.currency === currency);
            let lastTimestamp = new Date("1970-01-01");
            //sum orders across classes and use estimated NAV when units are used
            const nettedOrderValue = ordersWithCurrency.reduce((total: number, v: TransferAgentOrder) => {
                // calculate amount - amount or units * class.navPerUnit
                let amount: number = parseFloat(v.amount);
                const units = parseFloat(v.units);
                if (units > 0) {
                    // units case
                    let navPerUnit = 0;
                    // find navPerUnit from nav if exist
                    const nav: Nav = data.navs.find((n: Nav) => n.clientId === fund._id);
                    if (nav) {
                        const nfc: NavFundClass = nav.data.classes.find(
                            (c: NavFundClass) => c.name === v.instrument.name && c.navCurrency === currency
                        );
                        navPerUnit = parseFloat(nfc.navPerUnit);
                    } else {
                        const fic = fund.fundInfo.classes.find((c: PartyFundInfoClass) => c.instrumentId === v.instrument._id);
                        navPerUnit = parseFloat(fic.startNAVPerUnit);
                    }
                    amount = units * navPerUnit;
                }
                // SELL
                if (v.transactionType === TransferAgentOrderEnum.SELL) {
                    amount = amount * -1;
                }
                // lastTimestamp
                const lastOrder = new Date(v.createTimestamp);
                if (lastOrder > lastTimestamp) {
                    lastTimestamp = lastOrder;
                }
                return total + amount;
            }, 0);

            // The value in SEK. The fx rate of a NAV class converts its currency to the accounting currency of the fund, which is SEK.
            // Without a NAV class in the currency there is no rate, and the row is left out of the total
            let nettedOrderValueSek: number | undefined = nettedOrderValue;
            if (currency !== CurrencyEnum.SEK) {
                const navWithRate: Nav = data.navs.find((n: Nav) => n.clientId === fund._id);
                const classWithRate = navWithRate?.data.classes.find((c: NavFundClass) => c.navCurrency === currency);
                nettedOrderValueSek = classWithRate ? nettedOrderValue * parseFloat(classWithRate.fxRate) : undefined;
            }

            return {
                name: fund.name,
                clientId: fund._id,
                nettedOrderValue,
                nettedOrderValueSek,
                currency,
                lastTimestamp: lastTimestamp.toISOString().slice(0, 16).replace("T", " ")
            };
        });

        datarows.push(...rows);
    });

    // One total in SEK of all the funds and currencies
    const totalNettedOrderValueSek = datarows.reduce((total: number, row) => total + (row.nettedOrderValueSek ?? 0), 0);
    const rowsWithoutFxRate = datarows.filter((row) => typeof row.nettedOrderValueSek === "undefined").length;

    return (
        <div>
            <div className="flex mb-2">
                <DateForm defaultDateString={tradeDate} dateName={"tradeDate"}></DateForm>
            </div>
            <div className="mb-2">
                <b>Total netted order value: {twoDecPriceFormat(totalNettedOrderValueSek)} SEK</b>
                {rowsWithoutFxRate > 0 && (
                    <div style={{ color: "#b45309" }}>
                        {rowsWithoutFxRate} row{rowsWithoutFxRate === 1 ? " is" : "s are"} in a currency without an exchange rate and not in
                        the total
                    </div>
                )}
            </div>
            <div className="flex flex-wrap -mx-2">
                <div className="w-full px-2 mb-4">
                    <ReactTable columns={columns} data={datarows} />
                </div>
            </div>
        </div>
    );
}
